/**
 * Mídia do produto (Plano Loja/Twotrees, Fase 2 — `product_media`, 0019/0041)
 * · mesma autoridade da Plataforma da camada comercial (`CommercialEditForm`).
 *
 * Sem isto, Hero/vitrine/PDP/mega menu da Loja (Fases 3-5 do plano) dependeriam
 * de alguém escrever `product_media` direto no banco — não é "refletir de
 * verdade" como pedido pelo cliente.
 *
 * Mesmo padrão de injeção por prop (`edit`) do `CommercialEditForm.ts`: sem o
 * handler a tela é só leitura e DIZ isso, em vez de oferecer um botão que não
 * grava nada. Upload de arquivo binário fica FORA de escopo desta fase — o
 * operador informa a URL de um arquivo já hospedado (decisão registrada no
 * relatório de fase, não um bloqueio de código).
 *
 * `spin_frame` é o ângulo fixo que, em sequência (≥12 por produto), vira o
 * viewer 360° do Hero da Loja (Fase 3) — cadastrar é o mesmo fluxo de imagem,
 * só muda o `kind`.
 */
import { createElement as h, useState, type FormEvent, type ReactElement } from "react";
import { FileText, Image as ImageIcon, RotateCw, Video, type LucideIcon } from "lucide-react";
import { StatusPanel } from "./StatusPanel.js";
import { formatCount } from "../format.js";
import type { MediaCreatePatch, MediaUpdatePatch } from "../api/platform-client.js";
import type { PlatformCatalogProduct } from "../api/types.js";

type MediaItem = PlatformCatalogProduct["commercial"]["media"][number];

const MEDIA_KINDS: Array<{ value: MediaCreatePatch["kind"]; label: string }> = [
  { value: "image", label: "Imagem" },
  { value: "video", label: "Vídeo" },
  { value: "manual", label: "Manual" },
  { value: "pdf", label: "PDF" },
  { value: "document", label: "Documento" },
  { value: "spin_frame", label: "Frame de giro 360° (Hero)" }
];

const KIND_LABEL = new Map(MEDIA_KINDS.map((kind) => [kind.value as string, kind.label]));

export interface MediaEditHandlers {
  add: (patch: MediaCreatePatch) => void;
  update: (mediaId: string, patch: MediaUpdatePatch) => void;
  remove: (mediaId: string) => void;
  pending: boolean;
  error: Error | null;
  saved: boolean;
}

export interface MediaEditFormProps {
  product: PlatformCatalogProduct;
  edit?: MediaEditHandlers;
}

/** Só o que o formulário precisa do `FormData` — torna o submit testável. */
export interface MediaFormValues {
  get(name: string): unknown;
}

/**
 * Caminho de submit REAL do formulário de adicionar mídia, isolado do evento
 * de DOM. Devolve o patch enviado, ou `null` quando a validação recusa antes
 * de chamar o servidor (kind desconhecido, URL vazia) — mesma convenção de
 * `submitCommercialForm`.
 */
export function submitAddMediaForm(values: MediaFormValues, edit: MediaEditHandlers): MediaCreatePatch | null {
  const kind = values.get("kind");
  const url = values.get("url");
  const title = values.get("title");
  if (typeof kind !== "string" || !KIND_LABEL.has(kind)) return null;
  if (typeof url !== "string" || !url.trim()) return null;
  const patch: MediaCreatePatch = { kind: kind as MediaCreatePatch["kind"], url: url.trim() };
  if (typeof title === "string" && title.trim()) patch.title = title.trim();
  edit.add(patch);
  return patch;
}

const CONTROL =
  "w-full rounded-control border border-border-strong bg-surface px-3 py-2 text-sm text-ink " +
  "placeholder:text-muted focus-visible:outline-none aria-[invalid=true]:border-danger";


const KIND_ICON: Record<string, LucideIcon> = { image: ImageIcon, video: Video, manual: FileText, pdf: FileText, document: FileText, spin_frame: RotateCw };

/** Miniatura: imagem de verdade quando carrega; senão o ícone do tipo (nunca um ícone de imagem quebrada). */
function MediaThumb({ item }: { item: MediaItem }): ReactElement {
  const [failed, setFailed] = useState(false);
  const Icon = KIND_ICON[item.kind] ?? FileText;
  const showImage = item.kind === "image" && !failed;
  return h(
    "div",
    { className: "grid aspect-[4/3] w-full place-items-center overflow-hidden rounded-control bg-surface-soft text-muted" },
    showImage
      ? h("img", { src: item.url, alt: item.title || "Imagem do produto", loading: "lazy", onError: () => setFailed(true), className: "h-full w-full object-cover" })
      : h(Icon, { className: "h-10 w-10", "aria-hidden": true, focusable: false })
  );
}

function MediaCard({ item, rows, edit }: { item: MediaItem; rows: MediaItem[]; edit?: MediaEditHandlers }): ReactElement {
  return h(
    "li",
    { className: "flex flex-col gap-3 rounded-panel border border-border bg-surface p-3", "data-record-id": item.id },
    h(MediaThumb, { item }),
    h(
      "div",
      { className: "flex min-w-0 flex-col gap-1" },
      h(
        "div",
        { className: "flex items-center justify-between gap-2" },
        h("span", { className: "inline-flex rounded-pill bg-surface-soft px-2.5 py-0.5 text-xs font-bold text-ink" }, KIND_LABEL.get(item.kind) ?? item.kind),
        h("span", { className: "text-xs text-muted" }, `Posição ${formatCount(item.position)}`)
      ),
      h("p", { className: "m-0 truncate text-sm font-extrabold text-ink" }, item.title || "Sem título"),
      h("p", { className: "m-0 break-all text-xs text-muted", title: item.url }, item.url)
    ),
    h(MediaActionCell, { item, rows, edit })
  );
}

function MediaActionCell({ item, rows, edit }: { item: MediaItem; rows: MediaItem[]; edit?: MediaEditHandlers }): ReactElement | null {
  if (!edit) return null;
  const sorted = [...rows].sort((a, b) => a.position - b.position);
  const index = sorted.findIndex((row) => row.id === item.id);
  const prev = index > 0 ? sorted[index - 1] : null;
  const next = index < sorted.length - 1 ? sorted[index + 1] : null;
  const BTN = "inline-flex min-h-[32px] items-center justify-center rounded-control border border-border-strong px-2 text-xs font-semibold text-body hover:bg-surface-soft disabled:opacity-50";
  return h(
    "div",
    { className: "flex flex-wrap gap-1" },
    h(
      "button",
      {
        type: "button",
        className: BTN,
        disabled: edit.pending || !prev,
        "data-action": `media-move-up-${item.id}`,
        onClick: () => prev && edit.update(item.id, { position: prev.position })
      },
      "Mover ▲"
    ),
    h(
      "button",
      {
        type: "button",
        className: BTN,
        disabled: edit.pending || !next,
        "data-action": `media-move-down-${item.id}`,
        onClick: () => next && edit.update(item.id, { position: next.position })
      },
      "Mover ▼"
    ),
    h(
      "button",
      {
        type: "button",
        className: `${BTN} border-danger text-danger`,
        disabled: edit.pending,
        "data-action": `media-remove-${item.id}`,
        onClick: () => edit.remove(item.id)
      },
      "Remover"
    )
  );
}

export function MediaEditForm({ product, edit }: MediaEditFormProps): ReactElement {
  const rows = product.commercial.media;

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!edit) return;
    const form = event.currentTarget;
    const result = submitAddMediaForm(new FormData(form), edit);
    if (result) form.reset();
  }

  return h(
    "div",
    { className: "media-edit-form flex flex-col gap-4", "data-section": "midia-edicao" },
    rows.length === 0
      ? h(
          "p",
          { className: "record-table__empty m-0 rounded-panel border border-dashed border-border-strong px-4 py-6 text-center text-sm text-muted", role: "status" },
          "Nenhuma mídia cadastrada pela Plataforma."
        )
      : h(
          "ul",
          { className: "m-0 grid list-none grid-cols-1 gap-4 p-0 sm:grid-cols-2 xl:grid-cols-3", "aria-label": "Imagens, vídeos e manuais do produto" },
          [...rows]
            .sort((x, y) => x.position - y.position)
            .map((item) => h(MediaCard, { key: item.id, item, rows, edit }))
        ),
    edit
      ? h(
          "form",
          {
            className: "media-add-form flex flex-col gap-3 rounded-panel border border-border bg-surface-soft p-4",
            "data-form": "add-media",
            onSubmit
          },
          h("h4", { className: "text-base font-extrabold text-ink" }, "Adicionar mídia"),
          h(
            "div",
            { className: "grid grid-cols-1 gap-3 sm:grid-cols-3" },
            h(
              "div",
              { className: "flex flex-col gap-1" },
              h("label", { htmlFor: "media-kind", className: "text-sm font-semibold text-ink" }, "Tipo"),
              h(
                "select",
                { id: "media-kind", name: "kind", defaultValue: "image", disabled: edit.pending, className: CONTROL, "data-field": "kind" },
                ...MEDIA_KINDS.map((kind) => h("option", { key: kind.value, value: kind.value }, kind.label))
              )
            ),
            h(
              "div",
              { className: "flex flex-col gap-1 sm:col-span-2" },
              h("label", { htmlFor: "media-url", className: "text-sm font-semibold text-ink" }, "URL"),
              h("input", { id: "media-url", name: "url", type: "url", required: true, disabled: edit.pending, className: CONTROL, placeholder: "https://…", "data-field": "url" })
            ),
            h(
              "div",
              { className: "flex flex-col gap-1 sm:col-span-3" },
              h("label", { htmlFor: "media-title", className: "text-sm font-semibold text-ink" }, "Título (opcional)"),
              h("input", { id: "media-title", name: "title", type: "text", disabled: edit.pending, className: CONTROL, "data-field": "title" })
            )
          ),
          h(
            "button",
            {
              type: "submit",
              disabled: edit.pending,
              "data-action": "add-media",
              className:
                "inline-flex min-h-[44px] w-fit items-center justify-center rounded-control bg-brand px-4 py-2 " +
                "text-sm font-semibold text-on-brand transition-colors duration-fast ease-s2 " +
                "hover:bg-brand-pressed focus-visible:outline-none disabled:opacity-70"
            },
            edit.pending ? "Gravando…" : "Adicionar mídia"
          )
        )
      : h(StatusPanel, {
          kind: "awaiting_source",
          title: "Gravação de mídia não montada nesta visualização",
          detail: "A lista acima reflete o banco; esta montagem é só leitura."
        }),
    edit?.error ? h(StatusPanel, { kind: "error", title: "A mídia não foi gravada", detail: edit.error.message }) : null,
    edit?.saved ? h(StatusPanel, { kind: "empty", title: "Mídia atualizada e registrada no histórico" }) : null
  );
}
